import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { OfferPdfViewer } from "../components/OfferPdfViewer";
import { SignaturePad } from "../components/SignaturePad";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:30801");

async function fetchOffer(token: string) {
  const r = await fetch(`${API_BASE}/api/public/offer/${token}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function respondOffer(token: string, action: "accepted" | "declined", typedName: string, signatureDrawn: string | null, consentChecked: boolean) {
  const r = await fetch(`${API_BASE}/api/public/offer/${token}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, typed_name: typedName, signature_drawn: signatureDrawn, consent_checked: consentChecked }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error((err as any).detail || "Failed to respond");
  }
  return r.json();
}

export default function OfferAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const [responding, setResponding] = useState<"accepted" | "declined" | null>(null);
  const [responded, setResponded] = useState<"accepted" | "declined" | null>(null);
  const [error, setError] = useState("");
  const [readToEnd, setReadToEnd] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [signatureDrawn, setSignatureDrawn] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: ["publicOffer", token],
    queryFn: () => fetchOffer(token!),
    enabled: !!token,
    retry: 1,
  });
  const d = rawData as any;

  const pdfUrl = `${API_BASE}/api/public/offer/${token}/pdf`;

  async function handle(action: "accepted" | "declined") {
    setResponding(action);
    setError("");
    try {
      await respondOffer(token!, action, typedName.trim(), action === "accepted" ? signatureDrawn : null, action === "accepted" ? consentChecked : false);
      setResponded(action);
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setResponding(null);
    }
  }

  const card: React.CSSProperties = {
    background: "white", borderRadius: 16, padding: 32,
    boxShadow: "0 4px 32px rgba(0,0,0,0.10)", maxWidth: 760, width: "100%", margin: "0 auto",
  };

  const finalStatus = responded || d?.status;
  const todayIso = new Date().toISOString().slice(0, 10);
  const isExpired = !!d?.expiry_date && d.expiry_date < todayIso;
  const canAccept = readToEnd && !!typedName.trim() && consentChecked && !responding;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <LoadingOverlay active={!!responding} />
      {/* Header */}
      <div style={{ margin: "24px 0 32px", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>HR</span>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{d?.company_name || "HRxAI"}</span>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>Offer Letter</div>
      </div>

      {isLoading && (
        <div style={card}>
          <div style={{ textAlign: "center", padding: "32px 0", color: "#64748b" }}>Loading…</div>
        </div>
      )}

      {!isLoading && isError && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Link Not Found</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This offer link is invalid or has expired. Please contact the recruiter.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && (finalStatus === "accepted" || finalStatus === "declined") && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{finalStatus === "accepted" ? "🎉" : "👋"}</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 22 }}>
              {finalStatus === "accepted" ? "Offer Accepted!" : "Offer Declined"}
            </h2>
            <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
              {finalStatus === "accepted"
                ? "Congratulations! The recruiter has been notified and will be in touch with next steps."
                : "Thank you for letting us know. The recruiter has been notified."}
            </p>
            {finalStatus === "accepted" && (
              <a href={pdfUrl} style={{ display: "inline-block", padding: "10px 20px", borderRadius: 8, background: "#1e3a8a", color: "white", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
                ⬇ Download Signed Offer (PDF)
              </a>
            )}
          </div>
        </div>
      )}

      {!isLoading && !isError && d && !["accepted", "declined"].includes(finalStatus || "") && isExpired && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏰</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Offer Expired</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
              This offer expired on {d.expiry_date} and can no longer be accepted. Please contact the recruiter if you'd still like to proceed.
            </p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d && !["accepted", "declined"].includes(finalStatus || "") && !isExpired && (
        <div style={card}>
          {/* Offer header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ margin: "0 0 4px", color: "#1e3a8a", fontSize: 20 }}>
                Offer for {d.job_title || "Open Position"}
              </h2>
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
                Hi <strong>{d.candidate_name}</strong> — {d.company_name} is pleased to offer you this position.
              </p>
            </div>
            <a href={pdfUrl} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 8, border: "1px solid #1e3a8a", color: "#1e3a8a", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>
              ⬇ Download PDF
            </a>
          </div>

          {/* Details */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {d.job_title && <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>{d.job_title}</span>}
            {d.department && <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", fontSize: 12 }}>{d.department}</span>}
            {d.salary && <span style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>{d.currency_symbol}{Number(d.salary).toLocaleString()}/mo</span>}
            {d.start_date && <span style={{ background: "#fefce8", color: "#854d0e", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 10px", fontSize: 12 }}>Starts {d.start_date}</span>}
            {d.expiry_date && <span style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 10px", fontSize: 12 }}>Expires {d.expiry_date}</span>}
          </div>

          {/* The actual offer document */}
          <div style={{ marginBottom: 8 }}>
            <OfferPdfViewer url={pdfUrl} onReadToEnd={() => setReadToEnd(true)} />
          </div>
          {!readToEnd && (
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
              ↓ Scroll through the full offer letter to enable your response
            </p>
          )}

          {/* Signature */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px", margin: "16px 0" }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>
              Sign to accept
            </label>
            <input
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              placeholder="Type your full legal name"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14, border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box", marginBottom: 12 }}
            />
            <SignaturePad onChange={setSignatureDrawn} />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#475569", marginTop: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              I agree that clicking below constitutes my electronic signature on this document.
            </label>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Accept / Decline */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => handle("accepted")}
              disabled={!canAccept}
              style={{
                flex: 1, padding: "14px 0", borderRadius: 10, border: "none",
                background: !canAccept ? "#94a3b8" : "linear-gradient(135deg,#15803d,#22c55e)",
                color: "white", fontWeight: 700, fontSize: 15,
                cursor: !canAccept ? "not-allowed" : "pointer",
              }}>
              {responding === "accepted" ? "Submitting…" : "✅ Sign & Accept Offer"}
            </button>
            <button
              onClick={() => handle("declined")}
              disabled={!!responding || !readToEnd}
              style={{
                flex: 1, padding: "14px 0", borderRadius: 10,
                border: "2px solid #fecaca", background: "white",
                color: (!!responding || !readToEnd) ? "#fca5a5" : "#dc2626", fontWeight: 700, fontSize: 15,
                cursor: (!!responding || !readToEnd) ? "not-allowed" : "pointer",
              }}>
              {responding === "declined" ? "Submitting…" : "Decline"}
            </button>
          </div>

          <p style={{ margin: "16px 0 0", fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            By signing and clicking Accept, you confirm your agreement to this offer. The recruiter will be notified immediately.
          </p>
        </div>
      )}
    </div>
  );
}
